// socialProviders — single source of truth for WHICH social sign-in providers
// render on the login + profile surfaces, their display copy, AND their brand
// treatment (background color + text color; the inline-SVG glyph lives in
// src/components/SocialGlyph.jsx, keyed by the same provider id).
//
// D-283 (2026-07-20): the social connection set is Facebook + X (Twitter).
// Google is HIDDEN for now. Its full OAuth wiring stays intact everywhere —
// signInWithOAuth('google') and linkIdentity('google') in AuthContext, plus the
// provider-agnostic complete-signup link/create resolver — so re-enabling Google
// later is a ONE-LINE change: add 'google' back to ENABLED_SOCIAL below. No
// rebuild, no re-wiring. Phone OTP remains the PRIMARY login path; these buttons
// are additive.
//
// Both PlayerLoginPage and ProfileEditPage read this module so the two surfaces
// can never drift on which providers are live OR how they are branded.

// Per-provider display metadata. `provider` is the exact string handed to
// supabase.auth.signInWithOAuth / linkIdentity (Supabase's provider id — note
// X uses the legacy 'twitter' id). Kept for ALL providers, including the
// currently-hidden Google, so nothing has to be reconstructed on re-enable.
//
// `brand` carries each provider's official button colors so the buttons match
// what users expect (and, for Facebook, so Meta's app review sees a correct
// "Continue with Facebook" button). The matching inline-SVG glyph is rendered
// by src/components/SocialGlyph.jsx, keyed off the same provider id.
//   - Facebook: #1877F2 field, white text + white "f" glyph (Facebook brand).
//   - X (Twitter): #000000 field, white text + white X glyph (X brand).
// Contrast: white on #000000 is 21:1 (AA/AAA). White on #1877F2 is ~3.5:1 —
// this is Facebook's own official button pairing; it clears AA for large/bold
// text and is the brand-mandated color, so we keep it and render the label bold.
export const SOCIAL_PROVIDERS = {
  google: {
    provider: 'google',
    loginLabel: 'Continue with Google',
    profileLabel: 'Link Google Account',
    displayName: 'Google',
    // Google's brand button is a white field with dark text + a hairline
    // border (kept ready for the one-line re-enable, though Google is hidden).
    brand: { bgColor: '#FFFFFF', textColor: '#1F1F1F', borderColor: '#DADCE0' },
  },
  facebook: {
    provider: 'facebook',
    loginLabel: 'Continue with Facebook',
    profileLabel: 'Link Facebook',
    displayName: 'Facebook',
    brand: { bgColor: '#1877F2', textColor: '#FFFFFF', borderColor: null },
    // We request ONLY public_profile (name + picture). The owner did NOT
    // configure the `email` permission on the Facebook app, so requesting the
    // Supabase-default `email` scope yields Facebook's "Invalid Scopes: email"
    // developer error and blocks the whole link/login. Name + picture is all we
    // need for the profile photo. `scopes` is a single space-delimited string,
    // exactly what supabase.auth.signInWithOAuth / linkIdentity `options.scopes`
    // expects. Providers WITHOUT a `scopes` key fall through to the Supabase
    // default (see scopesFor + AuthContext) — unchanged behavior.
    scopes: 'public_profile',
  },
  twitter: {
    provider: 'twitter', // Supabase's provider id for X (formerly Twitter)
    loginLabel: 'Continue with X',
    profileLabel: 'Link X',
    displayName: 'X',
    brand: { bgColor: '#000000', textColor: '#FFFFFF', borderColor: null },
  },
}

// The ONLY switch for which social buttons render. Order here is render order.
// LAUNCH (2026-08-02): ALL social buttons are HIDDEN — phone OTP is the only
// login path shown. Offering Facebook/X sign-in without "Sign in with Apple"
// risks an App Store 4.8 rejection, and the brand-new-account social-link UX is
// unfinished. The full OAuth wiring (SOCIAL_PROVIDERS metadata, brand/scopes
// helpers, signInWithOAuth/linkIdentity in AuthContext) stays intact — both
// render surfaces self-hide on an empty list — so re-enabling any provider is a
// ONE-LINE change: add its key back to this array (no rebuild, no re-wiring).
export const ENABLED_SOCIAL = []

// Neutral fallback brand for an unknown / un-branded provider id — reuses the
// shell surface color so a mis-keyed provider still renders a readable button
// instead of an unstyled one.
const DEFAULT_BRAND = { bgColor: '#1A2231', textColor: '#FFFFFF', borderColor: null }

// Pure provider -> brand mapping. Takes a Supabase provider id (the same string
// used as the SOCIAL_PROVIDERS key) and returns its brand object, falling back
// to DEFAULT_BRAND for anything unmapped. Kept as a standalone helper so the
// two render surfaces share one accessor and the mapping stays unit-testable.
export const brandFor = (provider) =>
  SOCIAL_PROVIDERS[provider]?.brand ?? DEFAULT_BRAND

// Pure provider -> button style object. Returns the full inline `style` for a
// social button so BOTH render surfaces (PlayerLoginPage on `otp-page`,
// ProfileEditPage on plain `page`) stay pixel-identical.
//
// display/alignItems/justifyContent/gap are set EXPLICITLY here — they cannot
// be inherited from the base `.btn` rule (inline-flex + gap:8) because
// `.otp-page .btn { display: block }` (src/styles/shell-otp.css) overrides it
// on the login surface, and `gap` has no effect on a block container. Without
// this the glyph and label jam together with no space + baseline misalignment.
// Setting inline-flex + gap here survives the override on both surfaces.
export const socialButtonStyle = (provider) => {
  const brand = brandFor(provider)
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    background: brand.bgColor,
    color: brand.textColor,
    border: brand.borderColor ? `1px solid ${brand.borderColor}` : 'none',
    fontWeight: 600,
  }
}

// Pure provider -> OAuth scopes string (or undefined). Returns the per-provider
// `scopes` when one is declared (Facebook => 'public_profile'), else undefined
// so the caller omits `options.scopes` entirely and Supabase applies its default
// scope set (unchanged behavior for X/Google/others). Provider-agnostic — read
// this in AuthContext instead of hardcoding a 'facebook' branch.
export const scopesFor = (provider) => SOCIAL_PROVIDERS[provider]?.scopes

// Ordered metadata for the enabled providers, ready to map over in JSX.
export const enabledSocialProviders = () =>
  ENABLED_SOCIAL.map((key) => SOCIAL_PROVIDERS[key])
